<template>
    <AppButton :id="subscribeButtonID" :variant="subscribeBtnVariant" block class="my-4" @click="handleSubscribe">
        <Icon
            v-if="planTransitionType === 'downgrade' || planTransitionType === 'downgrade-pending'"
            name="tabler:arrow-down"
            size="24"
        />
        {{ subscriptionButtonText }}
    </AppButton>
</template>

<script setup lang="ts">
import { usePricingSubscribe } from "../../store/usePricingSubscribe";
import type { IPricingItem, TButtonVariants, TTransactionType } from "../../types";
import { useUserStore } from "~/store/global/user.store";

const { t } = useI18n();
interface IProps {
    plansDetails: IPricingItem;
    planTransitionType: TTransactionType;
}

const props = defineProps<IProps>();

const { activeCycle } = storeToRefs(usePricingStore());
const { userData } = storeToRefs(useUserStore());
const { handleSubscribe } = usePricingSubscribe(props);

const auth = useAuth();
const subscribeBtnVariant = computed((): TButtonVariants => {
    let variantsList: Record<TTransactionType, TButtonVariants> = {
        none: "outlined",
        downgrade: "ghost",
        upgrade: props.plansDetails.is_recommended ? "primary" : "outlined",
        "downgrade-pending": "ghost",
        "credit-pack": "outlined",
    };

    if (!auth.value || userData.value.free_plan) {
        return props.plansDetails.is_recommended ? "primary" : "outlined";
    }

    return variantsList[props.planTransitionType] || "outlined";
});

const subscriptionButtonText = computed(() => {
    let textBasedOnTransactionType = {
        none: activeCycle.value === "yearly" ? t("pages_pricing_getYearly") : t("pages_pricing_getMonthly"),
        downgrade: t("pages_pricing_downgrade"),
        upgrade: `${t("global_upgrade")} ${
            props.plansDetails.coupon_percentage ? `${props.plansDetails.coupon_percentage} % OFF` : ""
        }`,
        "downgrade-pending": t("pages_pricing_downgrade"),
        "credit-pack": `+ ${t("pages_pricing_morePhotos")}`,
    };
    return textBasedOnTransactionType[props.planTransitionType] || "";
});

const route = useRoute();
const subscribePlanButtonGtmID = computed(() => {
    return props.planTransitionType !== "none"
        ? `${props.plansDetails.cycle}-${props.planTransitionType}-${props.plansDetails.name}`
        : `${props.plansDetails.cycle}-${props.plansDetails.name}`;
});
const subscribeButtonID = computed(() => {
    if (route.fullPath.includes("pricing")) return `pricingpage-${subscribePlanButtonGtmID.value}-button`;
    if (route.fullPath.includes("renovation"))
        return `homerenovation-pricingsection-${subscribePlanButtonGtmID.value}-button`;
    if (route.fullPath.includes("photo")) return `photoediting-pricingsection-${subscribePlanButtonGtmID.value}-button`;
    if (route.fullPath.includes("design"))
        return `interiordesign-pricingsection-${subscribePlanButtonGtmID.value}-button`;
    if (route.fullPath.includes("virtual"))
        return `virtualstaging-pricingsection-${subscribePlanButtonGtmID.value}-button`;
    return `homepage-pricingsection-${subscribePlanButtonGtmID.value}-button`;
});
</script>
